import type { IPack, IPackDeadline } from './types.js';

/** A deadline worked out from a document's facts, for a person to check. */
export interface IDeadlineEstimate {
  deadline: IPackDeadline;
  /** The date, when the fact that sets the number of days is known (YYYY-MM-DD). */
  date?: string;
  days?: number;
  /** The value that set the number of days, e.g. "In Canada". */
  choice?: string;
  /** When that fact isn't known yet: the date for each of its values, so a person can pick. */
  options: { choice: string; days: number; date: string }[];
}

/** "2026-09-15" + 15 days → "2026-09-30" (calendar days; weekends and holidays aren't skipped). */
export function addDays(date: string, days: number): string | undefined {
  const m: RegExpMatchArray | null = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) {
    return undefined;
  }
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days)).toISOString().slice(0, 10);
}

/** "In Canada" → "in Canada", for the middle of a sentence. */
function lower(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

function same(a: string | undefined, b: string): boolean {
  return (a ?? '').trim().toLowerCase() === b.toLowerCase();
}

/**
 * The deadlines a document's facts start, worked out for a person to confirm: none when the start
 * date is missing or the deadline doesn't apply (e.g. the application was approved). An unknown
 * outcome still gets the deadline: missing one costs far more than checking one.
 */
export function deadlinesFor(pack: IPack, docTypeId: string | undefined, extra: { [fieldId: string]: string } | undefined): IDeadlineEstimate[] {
  const own: string[] = pack.docTypes.filter(t => t.id === docTypeId)[0]?.fields ?? [];
  const out: IDeadlineEstimate[] = [];
  for (const d of pack.deadlines ?? []) {
    const start: string = extra?.[d.from]?.trim() ?? '';
    if (own.indexOf(d.from) === -1 || !addDays(start, 0)) {
      continue;
    }
    const condition: string | undefined = d.when ? extra?.[d.when.field]?.trim() : undefined;
    if (d.when && condition && !same(condition, d.when.value)) {
      continue;
    }
    const choice: string | undefined = Object.keys(d.days.values).filter(c => same(extra?.[d.days.field], c))[0];
    const options = Object.keys(d.days.values).map(c => ({ choice: c, days: d.days.values[c], date: addDays(start, d.days.values[c])! }));
    out.push(choice
      ? { deadline: d, date: addDays(start, d.days.values[choice]), days: d.days.values[choice], choice, options: [] }
      : { deadline: d, options });
  }
  return out;
}

/** One line for people: "The last day to seek leave … would be 2026-09-30 (15 days, in Canada)." */
export function deadlineWords(e: IDeadlineEstimate): string {
  const what: string = e.deadline.meaning.charAt(0).toUpperCase() + e.deadline.meaning.slice(1);
  if (e.date) {
    return `${what} would be ${e.date} (${e.days} days, ${lower(e.choice!)}). ${e.deadline.caveat}`;
  }
  return `${what} would be ${e.options.map(o => `${o.date} if ${lower(o.choice)} (${o.days} days)`).join(', or ')}. ${e.deadline.caveat}`;
}
